import { useMonitor } from "@suse-o11y/signal";
import React from "react";

import { Button } from "~/components/button";
import { DropdownMenu } from "~/components/dropdown-menu";
import { DropdownMenuContent } from "~/components/dropdown-menu/content";
import { DropdownMenuItem } from "~/components/dropdown-menu/item";
import { DropdownMenuTrigger } from "~/components/dropdown-menu/trigger";
import { IconEllipsisOutlined, IconErrorOutlined } from "~/components/icon";
import { TableCell } from "~/components/table/cell";
import { TableRow } from "~/components/table/row";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";
import { Truncated } from "~/components/truncated";
import { cx } from "~/tools/class-variance";
import { useOnInvalidFormFocus } from "~/tools/impulse-form/use-on-invalid-form-focus";
import type { Record } from "~/tools/record";

import type { DashboardVariableKind } from "../../common/dashboard-spec";

import type { VariableState } from "./_drawer-state";

const KIND_TO_TYPE = {
	TextVariable: "Text",
	ListVariable: "List",
} satisfies Record<DashboardVariableKind>;

export interface VariableRowProps extends React.ComponentPropsWithoutRef<typeof TableRow> {
	variable: VariableState;
	handle: React.ReactNode;
	onEdit?: VoidFunction;
	onDelete?: VoidFunction;
}

export const VariableRow = React.forwardRef<HTMLDivElement, VariableRowProps>(
	({ className, variable: state, handle, onEdit, onDelete, ...props }, ref) => {
		const monitor = useMonitor();

		const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
		const focusInvalidRef = useOnInvalidFormFocus(state, () => {
			setIsDropdownOpen(true);
		}) satisfies React.RefObject<HTMLButtonElement>;

		const variable = state.getInput(monitor);
		const error = state.getError(monitor);

		return (
			<TableRow
				ref={ref}
				{...props}
				className={cx("ring-grey-200 data-dragging:border-none data-dragging:ring-1", className)}
			>
				<TableCell className="empty:p-0">{handle}</TableCell>

				<TableCell className="gap-2">
					<Truncated className="code" el="code">
						{variable.name}
					</Truncated>

					{error && (
						<Tooltip disableHoverableContent lazy={false}>
							<TooltipTrigger>
								<span className="cursor-help text-red-500">
									<IconErrorOutlined height={20} width={16} />
								</span>
							</TooltipTrigger>

							<TooltipContent>{error}</TooltipContent>
						</Tooltip>
					)}
				</TableCell>

				<TableCell>
					<Truncated>{variable.label ?? variable.name}</Truncated>

					{error && (
						<Tooltip disableHoverableContent lazy={false}>
							<TooltipTrigger>
								<span className="cursor-help text-red-500">
									<IconErrorOutlined height={20} width={16} />
								</span>
							</TooltipTrigger>

							<TooltipContent>{error}</TooltipContent>
						</Tooltip>
					)}
				</TableCell>

				<TableCell>{KIND_TO_TYPE[variable.spec.kind]}</TableCell>

				<TableCell>{variable.description || "-"}</TableCell>

				<TableCell className="gap-1">
					<DropdownMenu onOpenChange={setIsDropdownOpen} open={isDropdownOpen}>
						<DropdownMenuTrigger asChild>
							<Button icon ref={focusInvalidRef} size="xs">
								<IconEllipsisOutlined className="text-[20px]" />
							</Button>
						</DropdownMenuTrigger>

						<DropdownMenuContent>
							<DropdownMenuItem asChild onSelect={onEdit}>
								<button type="button">Edit variable</button>
							</DropdownMenuItem>

							<DropdownMenuItem asChild onSelect={onDelete}>
								<button type="button">Delete variable</button>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</TableCell>
			</TableRow>
		);
	},
);
